import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getMeasureName } from 'core/utils/productUtils';
import { useEffect, useState } from 'react';
import type { ProjectProduct } from 'models/order/orderProject';
import type { FC } from 'react';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';

interface ProductOrderDialogProps {
  open: boolean;
  products: ProjectProduct[];
  onClose: VoidFunction;
  onSave: (orderedProducts: ProjectProduct[]) => Promise<void>;
}

const ProductOrderDialog: FC<ProductOrderDialogProps> = ({
  open,
  products,
  onClose,
  onSave,
}) => {
  const [localProducts, setLocalProducts] = useState<ProjectProduct[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalProducts([...products].sort((a, b) => a.positionIndex - b.positionIndex));
    }
  }, [open, products]);

  const moveProduct = (index: number, direction: 'up' | 'down'): void => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= localProducts.length) return;

    const updated = [...localProducts];

    const temp = updated[index];
    const target = updated[targetIndex];

    if (!temp || !target) {
      return;
    }

    updated[index] = target;
    updated[targetIndex] = temp;

    setLocalProducts(updated);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const withUpdatedPositions = localProducts.map((p, i) => ({
        ...p,
        positionIndex: i,
      }));
      await onSave(withUpdatedPositions);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (): void => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown={saving}>
      <DialogTitle>Настройка порядка товаров</DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <List disablePadding>
          {localProducts.map((product, index) => (
            <ListItem
              key={product.id}
              divider
              sx={{ pr: 12 }}
              secondaryAction={
                <Stack direction='row' gap={0.5}>
                  <Tooltip title='Переместить вверх'>
                    <span>
                      <IconButton
                        size='small'
                        disabled={index === 0 || saving}
                        onClick={() => {
                          moveProduct(index, 'up');
                        }}>
                        <ArrowUpwardIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Переместить вниз'>
                    <span>
                      <IconButton
                        size='small'
                        disabled={index === localProducts.length - 1 || saving}
                        onClick={() => {
                          moveProduct(index, 'down');
                        }}>
                        <ArrowDownwardIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }>
              <Box
                sx={{
                  minWidth: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                }}>
                <Typography variant='body2' color='text.secondary' fontWeight={600}>
                  {index + 1}
                </Typography>
              </Box>
              <ListItemText
                primary={product.name}
                secondary={`${product.count} ${getMeasureName(product.measure, true)}`}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Отмена
        </Button>
        <LoadingButton
          variant='contained'
          loading={saving}
          onClick={() => {
            intentionallyFloatingPromiseReturn(handleSave)();
          }}>
          Сохранить
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProductOrderDialog;
